import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';

const LIBRARY = '/dashboard/tools';

export function ToolBackLink({ to = LIBRARY }: { to?: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 font-body text-sm font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:underline"
    >
      ← Back to tools
    </Link>
  );
}

export function ToolPageHeading({
  title,
  subtitle,
  backTo = LIBRARY,
}: {
  title: string;
  subtitle: string;
  backTo?: string;
}) {
  return (
    <header className="space-y-3">
      <ToolBackLink to={backTo} />
      <div className="space-y-2">
        <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
          Tools
        </p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
          {title}
        </h1>
        <p className="max-w-3xl font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">{subtitle}</p>
      </div>
    </header>
  );
}

export function InfoTip({ text }: { text: string }) {
  return (
    <span className="inline-flex shrink-0" title={text}>
      <span className="flex size-4 cursor-help items-center justify-center rounded-full border border-white/20 bg-white/5 text-[10px] font-bold text-[var(--home-muted-on-dark)]">
        i
      </span>
      <span className="sr-only">{text}</span>
    </span>
  );
}

export function FieldLabel({
  htmlFor,
  tip,
  required,
  children,
}: {
  htmlFor: string;
  tip: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="flex items-start justify-between gap-2 font-body text-sm text-[var(--home-text-on-dark)]">
      <span className="leading-snug">
        {children}
        {required ? <span className="text-rose-400"> *</span> : null}
      </span>
      <InfoTip text={tip} />
    </label>
  );
}

export function InfoSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={`${SITE_GLASS_CARD} p-5 md:p-6`}>
      <h2 className="font-display text-sm font-bold text-[var(--home-text-on-dark)]">{title}</h2>
      <div className="mt-4 space-y-3 font-body text-sm leading-relaxed text-[var(--home-text-on-dark)]/90">{children}</div>
    </section>
  );
}

export function ExampleCallout({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 font-body text-sm italic text-[var(--home-muted-on-dark)]">
      {children}
    </div>
  );
}

export function PanelTitle({ children }: { children: ReactNode }) {
  return <h3 className="mb-4 font-display text-xs font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">{children}</h3>;
}

export function ToolTwoColumn({
  left,
  right,
  resetLabel = 'Reset calculator',
  onReset,
}: {
  left: ReactNode;
  right: ReactNode;
  resetLabel?: string;
  onReset: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
        <div className={`${SITE_GLASS_CARD} p-4 md:p-6`}>{left}</div>
        <div className={`${SITE_GLASS_CARD} p-4 md:p-6`}>{right}</div>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="w-full rounded-2xl border border-white/15 bg-white/[0.04] py-3.5 font-body text-sm font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/[0.08]"
      >
        {resetLabel}
      </button>
    </div>
  );
}

export function ResultsEmpty({ message }: { message: string }) {
  return (
    <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-3 text-center">
      <span className="text-4xl opacity-40" aria-hidden>
        🧮
      </span>
      <p className="max-w-xs font-body text-sm text-[var(--home-muted-on-dark)]">{message}</p>
    </div>
  );
}

export const toolInputClass = `w-full ${SITE_FORM_CONTROL}`;

export function fmtUsd(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtPct(n: number, digits = 2): string {
  return `${n.toFixed(digits)}%`;
}

/** Standard fixed-rate mortgage monthly payment. */
export function monthlyMortgagePayment(principal: number, annualRatePct: number, termYears: number): number {
  if (principal <= 0 || termYears <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  const n = Math.round(termYears * 12);
  if (n <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export function parseNum(raw: string): number {
  const n = Number(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}
